/**
 * Комментарии бренда (§7, вкладка внизу карточки).
 *
 * Тред: аватар 24 px, автор, дата, пометка «изменён». Новый комментарий тоже
 * уходит в черновик (помечен оранжевым) и публикуется с пакетом (П1).
 *
 * Правка своего комментария — 15 минут после создания; то же окно проверяет
 * сервер. Импортированные комментарии автора в системе не имеют: подпись у них
 * лежит в `authorLabel` («Импорт из Google-таблицы», «Дмитрий Иванов ·
 * 3 апреля») и показывается как есть — без нашей даты, потому что дата импорта
 * к содержанию отношения не имеет.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import type { ChannelCode, Comment } from '@shared/types.ts'
import {
  Avatar, Badge, Button, Checkbox, EmptyState, IconClose, IconSend, Menu, Modal, RichText,
  Skeleton, Textarea, wrapSelection, type MenuItem,
} from '../../../components/index.js'
import { api, ApiRequestError } from '../../../lib/api.js'
import { shortName } from '../../../lib/names.js'
import { useApp } from '../../../store/app-store.js'
import { cardTexts } from './texts.card.js'
import './CommentsBlock.css'

/** Окно правки своего комментария — 15 минут (§7, COMMENT_EDIT_WINDOW_MIN). */
const EDIT_WINDOW_MS = 15 * 60 * 1000

/** «10 апреля» — срок задачи словами. */
function dueLabel(due: string): string {
  const date = new Date(`${due}T12:00:00`)
  if (Number.isNaN(date.getTime())) return due
  return date.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' })
}

function dateLabel(iso: string): string {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleString('ru-RU', {
    day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit',
  })
}

export interface CommentsBlockProps {
  brandId: number
  /** Комментарий привязывается к активному каналу — как в импорте. */
  channel: ChannelCode
  /** Сколько комментариев сейчас известно карточке: для вкладки. */
  onCountChange?: (count: number) => void
}

export function CommentsBlock({ brandId, channel, onCountChange }: CommentsBlockProps) {
  const user = useApp((state) => state.bootstrap?.user ?? null)
  const addComment = useApp((state) => state.addComment)
  const completeTask = useApp((state) => state.completeTask)
  const setCommentKind = useApp((state) => state.setCommentKind)
  const toast = useApp((state) => state.toast)
  const reloadDraft = useApp((state) => state.reloadDraft)
  const draftComments = useApp((state) => state.overlay.draftComments)

  const [items, setItems] = useState<Comment[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [body, setBody] = useState('')
  const [busy, setBusy] = useState(false)
  const [removing, setRemoving] = useState<Comment | null>(null)
  /* На что отвечаем: цитата висит над полем ввода, как в любом чате. */
  const [replyTo, setReplyTo] = useState<Comment | null>(null)
  const inputRef = useRef<HTMLTextAreaElement | null>(null)

  const canWrite = Boolean(user?.permissions.includes('comments.create'))
  const canModerate = Boolean(user?.permissions.includes('comments.moderate'))

  const load = useCallback(async () => {
    try {
      const res = await api.comments(brandId)
      setItems(res.comments)
      setError(null)
      onCountChange?.(res.comments.length)
    } catch (err) {
      setItems([])
      setError(err instanceof ApiRequestError ? err.message : cardTexts.comments.loadFailed)
    }
  }, [brandId, onCountChange])

  useEffect(() => {
    setItems(null)
  }, [brandId])

  /* Черновик мог измениться в другом месте (ревью, Ctrl+Z) — перечитываем тред:
     удалённый из черновика комментарий должен исчезнуть и здесь. */
  useEffect(() => {
    void load()
  }, [draftComments, load])

  async function submit(): Promise<void> {
    const text = body.trim()
    if (text === '' || busy) return
    setBusy(true)
    await addComment(brandId, text, channel, 'comment', null, replyTo?.id ?? null)
    setBusy(false)
    setBody('')
    setReplyTo(null)
    await load()
    inputRef.current?.focus()
  }

  async function saveEdit(comment: Comment, next: string): Promise<void> {
    try {
      await api.editComment(comment.id, next)
      await Promise.all([reloadDraft(), load()])
    } catch (err) {
      toast('error', err instanceof ApiRequestError ? err.message : cardTexts.comments.loadFailed)
    }
  }

  async function remove(comment: Comment): Promise<void> {
    setRemoving(null)
    try {
      await api.deleteComment(comment.id)
      await Promise.all([reloadDraft(), load()])
    } catch (err) {
      toast('error', err instanceof ApiRequestError ? err.message : cardTexts.comments.loadFailed)
    }
  }

  /* Заметка оказалась поручением — переводим её в задачу, не заводя новую
     запись: автор и дата у неё уже правильные. */
  async function toTask(comment: Comment): Promise<void> {
    try {
      await setCommentKind(comment.id, comment.kind === 'task' ? 'comment' : 'task')
      await load()
    } catch (err) {
      toast('error', err instanceof ApiRequestError ? err.message : cardTexts.comments.loadFailed)
    }
  }

  async function finishTask(comment: Comment, report: string): Promise<void> {
    try {
      await completeTask(comment.id, report)
      await load()
    } catch (err) {
      toast('error', err instanceof ApiRequestError ? err.message : cardTexts.comments.loadFailed)
    }
  }

  if (items === null) {
    /* Пока грузится тред — скелетон 3 строк, как требует §11.1. Ширины свои:
       табличные колонки по умолчанию в панель 480 px не влезают. */
    return <Skeleton rows={3} columns={[180, 220]} label={cardTexts.state.loadingLabel} />
  }

  return (
    <div className="card-comments">
      {error ? <p className="card-comments__error t-meta">{error}</p> : null}

      {items.length === 0 && !error ? (
        <EmptyState title={cardTexts.comments.emptyTitle} text={cardTexts.comments.emptyText} />
      ) : (
        <ul className="card-comments__list">
          {items.map((comment) => (
            <CommentRow
              key={comment.id}
              comment={comment}
              mine={comment.authorId !== null && comment.authorId === user?.id}
              canModerate={canModerate}
              canWrite={canWrite}
              onSave={(next) => void saveEdit(comment, next)}
              onRemove={() => setRemoving(comment)}
              onToTask={() => void toTask(comment)}
              onDone={(report) => void finishTask(comment, report)}
              onReply={() => {
                setReplyTo(comment)
                inputRef.current?.focus()
              }}
            />
          ))}
        </ul>
      )}

      {canWrite ? (
        <div className="card-comments__form">
          {replyTo ? (
            <div className="card-comments__reply">
              <span className="card-comments__reply-text">
                <b>{shortName(replyTo.authorLabel)}</b>
                {` · ${replyTo.body.slice(0, 80)}`}
              </span>
              <button
                type="button"
                className="card-comments__reply-cancel"
                aria-label={cardTexts.comments.replyCancel}
                onClick={() => setReplyTo(null)}
              >
                <IconClose size={16} />
              </button>
            </div>
          ) : null}
          {/* Поле и отправка — одной строкой, как в мессенджере: кнопка справа,
              внутри поля, а не отдельным блоком под ним. */}
          <div className="composer">
            <Textarea
              ref={inputRef}
              className="composer__input"
              value={body}
              rows={1}
              maxLength={4000}
              commitOnEnter
              onCommit={() => void submit()}
              placeholder={cardTexts.comments.placeholder}
              aria-label={cardTexts.comments.placeholder}
              onChange={(event) => setBody(event.target.value)}
              onKeyDown={(event) => {
                /* Cmd/Ctrl+B, I, E — обрамление выделенного куска, как в любом
                   редакторе. Браузер на эти сочетания в поле ввода ничего не
                   делает, перехват ни у кого ничего не отнимает. */
                if (!event.metaKey && !event.ctrlKey) return
                const marker = event.key === 'b' ? '**'
                  : event.key === 'i' ? '*'
                    : event.key === 'e' ? '`'
                      : null
                if (marker === null) return
                event.preventDefault()
                const field = event.currentTarget
                const next = wrapSelection(body, field.selectionStart, field.selectionEnd, marker)
                setBody(next.value)
                window.requestAnimationFrame(() => {
                  field.setSelectionRange(next.start, next.end)
                  field.focus()
                })
              }}
            />
            <button
              type="button"
              className="composer__send"
              aria-label={cardTexts.comments.submit}
              title={cardTexts.comments.submitHint}
              disabled={body.trim() === '' || busy}
              onClick={() => void submit()}
            >
              <IconSend size={20} />
            </button>
          </div>
        </div>
      ) : (
        <p className="card-comments__denied t-meta">{cardTexts.comments.denied}</p>
      )}

      <Modal
        open={removing !== null}
        onClose={() => setRemoving(null)}
        title={cardTexts.comments.removeTitle}
        size="m"
        dismissible={false}
        footer={
          <>
            <Button variant="secondary" onClick={() => setRemoving(null)}>
              {cardTexts.comments.removeCancel}
            </Button>
            <Button
              variant="danger-ghost"
              onClick={() => {
                if (removing) void remove(removing)
              }}
            >
              {cardTexts.comments.removeConfirm}
            </Button>
          </>
        }
      >
        {cardTexts.comments.removeText}
      </Modal>
    </div>
  )
}

interface CommentRowProps {
  canWrite: boolean
  onToTask: () => void
  onDone: (report: string) => void
  onReply: () => void
  comment: Comment
  mine: boolean
  canModerate: boolean
  onSave: (body: string) => void
  onRemove: () => void
}

function CommentRow({
  comment, mine, canModerate, canWrite, onSave, onRemove, onToTask, onDone, onReply,
}: CommentRowProps) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(comment.body)
  /* Пока отчёт не написан, галочка не ставится: «выполнено» без ответа на
     вопрос «что сделано» ничего не говорит остальным. */
  const [closing, setClosing] = useState(false)
  const [report, setReport] = useState('')
  const [menuAt, setMenuAt] = useState<HTMLElement | null>(null)

  const age = Date.now() - new Date(comment.createdAt).getTime()
  const editable = mine && Number.isFinite(age) && age <= EDIT_WINDOW_MS
  /** Автора в системе нет — подпись готовая, кружок системный. */
  const imported = comment.authorId === null

  const menuItems: MenuItem[] = [
    { id: 'reply', label: cardTexts.comments.reply },
    ...(editable ? [{ id: 'edit', label: cardTexts.comments.edit }] : []),
    ...(canWrite && comment.doneAt === null && !comment.donePending && !comment.isDraft
      ? [{
        id: 'kind',
        label: (comment.kindPending ?? comment.kind) === 'task'
          ? cardTexts.comments.toComment
          : cardTexts.comments.toTask,
      }]
      : []),
    ...(mine || canModerate ? [{ id: 'remove', label: cardTexts.comments.remove, danger: true }] : []),
  ]

  /* Вид и галочка могут ждать публикации — показываем их применёнными,
     но с пометкой «в черновике». */
  const kind = comment.kindPending ?? comment.kind
  const done = comment.doneAt !== null || comment.donePending
  const openTask = kind === 'task' && !done
  const classes = ['card-comment']
  if (comment.isDraft) classes.push('card-comment--draft')
  if (openTask) classes.push('card-comment--task')
  if (kind === 'task' && done) classes.push('card-comment--done')

  return (
    <li className={classes.join(' ')}>
      {/* Все действия с записью — за троеточием: ссылки «Сделать задачей»
          и «Изменить» занимали больше места, чем сам комментарий. */}
      {!editing && menuItems.length > 0 ? (
        <>
          <button
            type="button"
            className="card-comment__more"
            aria-label={cardTexts.comments.more}
            title={cardTexts.comments.more}
            aria-haspopup="menu"
            onClick={(event) => setMenuAt(event.currentTarget)}
          >
            ⋯
          </button>
          {menuAt ? (
            <Menu
              open
              anchor={menuAt}
              label={cardTexts.comments.more}
              items={menuItems}
              minWidth={200}
              onClose={() => setMenuAt(null)}
              onSelect={(id) => {
                setMenuAt(null)
                if (id === 'reply') onReply()
                if (id === 'edit') setEditing(true)
                if (id === 'kind') onToTask()
                if (id === 'remove') onRemove()
              }}
            />
          ) : null}
        </>
      ) : null}
      <Avatar
        name={comment.authorLabel}
        size={24}
        system={imported}
        color={comment.authorColor ?? undefined}
      />
      <div className="card-comment__body">
        <p className="card-comment__meta t-meta">
          <span className="card-comment__author">{comment.authorLabel}</span>
          {/* Дата та, когда написано, а не когда перенесено: у записей из
              Google-таблицы это разные дни. */}
          <span> · {dateLabel(comment.writtenAt ?? comment.createdAt)}</span>
          {comment.editedAt ? <span> · {cardTexts.comments.editedMark}</span> : null}
          {comment.isDraft ? (
            <>
              {' '}
              <Badge variant="draft">{cardTexts.comments.draftMark}</Badge>
            </>
          ) : null}
          {kind === 'task' && comment.dueOn ? (
            <span className="card-comment__due"> · {cardTexts.comments.due(dueLabel(comment.dueOn))}</span>
          ) : null}
        </p>

        {comment.replyTo ? (
          <p className="card-comment__quote">
            <b>{shortName(comment.replyTo.author)}</b>
            {` · ${comment.replyTo.body.slice(0, 90)}`}
          </p>
        ) : null}

        {editing ? (
          <div className="card-comment__edit">
            <Textarea
              value={draft}
              rows={2}
              autoFocus
              maxLength={4000}
              commitOnEnter
              onCommit={() => {
                onSave(draft.trim())
                setEditing(false)
              }}
              aria-label={cardTexts.comments.edit}
              onChange={(event) => setDraft(event.target.value)}
            />
            <div className="card-comment__edit-actions">
              <Button
                variant="secondary"
                size="m"
                disabled={draft.trim() === ''}
                onClick={() => {
                  onSave(draft.trim())
                  setEditing(false)
                }}
              >
                {cardTexts.comments.editSave}
              </Button>
              <Button
                variant="ghost"
                size="m"
                onClick={() => {
                  setDraft(comment.body)
                  setEditing(false)
                }}
              >
                {cardTexts.comments.editCancel}
              </Button>
            </div>
          </div>
        ) : (
          <RichText className="card-comment__text t-body" text={comment.body} />
        )}

        {/* «Изменить» остаётся подписью — это редкое действие с окном в
            15 минут, и ссылку надо прочитать. Удаление — корзина в углу
            пузыря: подпись в две строки занимала больше места, чем сам
            комментарий. */}
        {kind === 'task' && done ? (
          <p className="card-comment__done t-meta">
            {comment.donePending
              ? cardTexts.comments.donePending
              : cardTexts.comments.doneBy(comment.doneByName ?? '—')}
          </p>
        ) : null}

        {/* Закрыть задачу может любой участник — работу делают разные люди. */}
        {openTask && canWrite ? (
          closing ? (
            <div className="card-comment__close">
              <Textarea
                rows={2}
                autoFocus
                value={report}
                placeholder={cardTexts.comments.reportPlaceholder}
                aria-label={cardTexts.comments.reportPlaceholder}
                onChange={(event) => setReport(event.target.value)}
              />
              <div className="card-comment__edit-actions">
                <Button
                  variant="primary"
                  size="m"
                  disabled={report.trim() === ''}
                  onClick={() => { onDone(report.trim()); setClosing(false); setReport('') }}
                >
                  {cardTexts.comments.closeTask}
                </Button>
                <Button variant="ghost" size="m" onClick={() => { setClosing(false); setReport('') }}>
                  {cardTexts.comments.editCancel}
                </Button>
              </div>
            </div>
          ) : (
            <Checkbox
              checked={false}
              label={<span className="t-meta">{cardTexts.comments.markDone}</span>}
              onChange={() => setClosing(true)}
            />
          )
        ) : null}


      </div>
    </li>
  )
}
